import React, { useEffect, useState, useMemo } from "react";
import {
  Modal,
  Slide,
  Stack,
  Typography,
  Box,
  Button,
  Select,
  InputLabel,
  MenuItem,
  Grid,
  Autocomplete,
  IconButton,
  Checkbox,
  FormControlLabel} from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";

import { ReactComponent as CloseIcon } from "../../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as InfoIcon } from "../../../../../vendor/_new/icon/sites/info.svg";
import { ReactComponent as ExceptIcon } from "../../../../../vendor/_new/icon/services/except.svg";
import { ReactComponent as ArrowdownIcon } from "../../../../../vendor/_new/icon/sites/arrow_down.svg";
import { ReactComponent as DuplicateIcon } from "../../../../../vendor/_new/icon/services/duplicate.svg";
import { ReactComponent as DeleteIcon } from "../../../../../vendor/_new/icon/services/delete.svg";
import { ReactComponent as PlusIcon } from "../../../../../vendor/_new/icon/sites/plus.svg";

import { SafeTextField, GreenSelectForm, GreenSwitch, GreenDateTimeLocalField } from "../../../../common/Greens";
import {
  FirewallAction,
  ExpressionKeyField,
  FireWallExpressionCondition,
  RateLimitMitigationTimeout,
  RateLimitPeriod,
  UserType,
  RateLimitMode,
  ChallengeKind,
  AuthFailureKind,
} from "../../../../../utils/constants";
import { getExpressionKeyTitle } from "../../../../../utils/constants";
import { getConditionTitle, getValidCondFlag } from "../../../application/rule/component";
import countries from "../../../application/rule/countries";
import colors from "../../../../../utils/colors";
import { FeatureId } from "../../../../../utils/constants";
import { RateLimitCharacteristicKey } from "../../../../../utils/constants";
import useAuth from "../../../../../hooks/useAuth";
import useRateLimit from "../../../../../hooks/user/useRateLimit";
import { useNotification } from "../../../../../contexts/NotificationContext";
import { validateRateLimitRuleForm, validateExpiresAt } from "../../../../../utils/validation";
import {
  getExpressionValueHint,
  isExpressionRowStarted,
  RATE_LIMIT_CHAR_HINTS,
  resolveVisibleCellError,
} from "../../../../../utils/validation/expressionValidators";
import userAxios from "../../../../../utils/axios/v1/userAxios";
import { containedButtonSx, SaveButton, formSaveButtonSx } from "../../../../common/Button";
import FormHelpTooltip from "../../../../common/FormHelpTooltip";
import { ExpressionFieldMessage } from "../../../../common/ExpressionFieldMessages";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../../super/design";

const menuItemSx = {
  height: 38,
  fontSize: 14,
  lineHeight: "21px",
  "&:hover": { color: colors.primary[300] },
};

const KeyComponent = ({ keyStr, selectKeyStr, padx, pady, advanced, error }) => {
  const aKeys = [
    ExpressionKeyField.SOURCE_IP,
    ExpressionKeyField.HOST_NAME,
    ExpressionKeyField.USER_AGENT,
    ExpressionKeyField.HEADER,
    ExpressionKeyField.QUERY,
    ExpressionKeyField.COOKIE,
    ExpressionKeyField.URI,
    ExpressionKeyField.REFERER,
    ExpressionKeyField.METHOD,
  ];
  const aAdvancedKeys = [
    ExpressionKeyField.COUNTRY,
    ExpressionKeyField.CITY_NAME,
    ExpressionKeyField.AS_NUMBER,
    ExpressionKeyField.BOT_SCORE,
    ExpressionKeyField.JA3_FINGERPRINT,
  ];
  return (
    <GreenSelectForm error={error}>
      <InputLabel id="field-information">Field</InputLabel>
      <Select labelId="field-information" fullWidth IconComponent={ArrowdownIcon} value={keyStr} onChange={selectKeyStr}>
        {keyStr === ExpressionKeyField.NONE && (
          <MenuItem
            key={`_key0_${padx}_${pady}`}
            value={ExpressionKeyField.NONE}
            disabled
            sx={{ ...menuItemSx, display: "none!important" }}
          >
            Select Field ...
          </MenuItem>
        )}
        {aKeys.map((keyValue, keyIdx) => (
          <MenuItem key={`_key${keyIdx + 1}_${padx}_${pady}`} value={keyValue} sx={menuItemSx}>
            {getExpressionKeyTitle(keyValue)}
          </MenuItem>
        ))}
        {(undefined === advanced || true === advanced) &&
          aAdvancedKeys.map((keyValue, keyIdx) => (
            <MenuItem key={`_key${keyIdx + 1 + aKeys.length}_${padx}_${pady}`} value={keyValue} sx={menuItemSx}>
              {getExpressionKeyTitle(keyValue)}
            </MenuItem>
          ))}
      </Select>
    </GreenSelectForm>
  );
};

const CondComponent = ({ condStr, selectCondStr, disableMenu, disabled, padx, pady, error }) => {
  // Order should NOT be modified
  const aConditions = [
    FireWallExpressionCondition.EQUALS,
    FireWallExpressionCondition.NOT_EQUALS,
    FireWallExpressionCondition.CONTAINS,
    FireWallExpressionCondition.NOT_CONTAINS,
    FireWallExpressionCondition.GREATER_THAN,
    FireWallExpressionCondition.LESS_THAN,
    FireWallExpressionCondition.GREATER_THAN_OR_EQUALS_TO,
    FireWallExpressionCondition.LESS_THAN_OR_EQUALS_TO,
  ];
  // switch
  return (
    <GreenSelectForm error={error}>
      <InputLabel id="operation-information">Operator</InputLabel>
      <Select
        fullWidth
        labelId="operation-information"
        IconComponent={ArrowdownIcon}
        disabled={disabled}
        value={condStr}
        onChange={selectCondStr}
      >
        {condStr === "none" && (
          <MenuItem key={"_cond0_" + padx + "_" + pady} value="none" disabled sx={{ ...menuItemSx, display: "none!important" }}>
            Select Item ...
          </MenuItem>
        )}
        {aConditions
          .filter((_, condIdx) => disableMenu[condIdx])
          .map((condValue, condIdx) => (
            <MenuItem key={`_cond${condIdx + 1}_${padx}_${pady}`} value={condValue} sx={menuItemSx}>
              {getConditionTitle(condValue)}
            </MenuItem>
          ))}
      </Select>
    </GreenSelectForm>
  );
};

const ActionComponent = ({ action, setAction }) => {
  return (
    <GreenSelectForm>
      <InputLabel id="action-selector">Choose action</InputLabel>
      <Select
        labelId="action-selector"
        IconComponent={ArrowdownIcon}
        value={action}
        onChange={(event) => {
          setAction(Number(event.target.value));
        }}
        fullWidth
      >
        <MenuItem key={"action_3"} value={FirewallAction.CHALLENGE} sx={menuItemSx}>
          Challenge
        </MenuItem>
        <MenuItem key={"action_4"} value={FirewallAction.BLOCK} sx={menuItemSx}>
          Block
        </MenuItem>
        <MenuItem key={"action_5"} value={FirewallAction.DROP} sx={menuItemSx}>
          Drop
        </MenuItem>
      </Select>
    </GreenSelectForm>
  );
};

const MethodValueComponent = ({ valueStr, selectValueStr, disabled, padx, pady, error }) => {
  const aMethods = ["GET", "POST", "HEAD", "PUT", "DELETE", "PATCH", "TRACE", "CONNECT"];
  return (
    <GreenSelectForm error={error}>
      <InputLabel id="method-value-information">Value</InputLabel>
      <Select
        labelId="method-value-information"
        IconComponent={ArrowdownIcon}
        fullWidth
        disabled={disabled}
        value={valueStr}
        onChange={selectValueStr}
      >
        {valueStr === "none" && (
          <MenuItem key={"_cond0_" + padx + "_" + pady} value="none" disabled sx={{ ...menuItemSx, display: "none!important" }}>
            Select Item ...
          </MenuItem>
        )}
        {aMethods.map((methodValue, methodIdx) => (
          <MenuItem key={`_cond${methodIdx + 1}_${padx}_${pady}`} value={methodValue} sx={{ menuItemSx }}>
            {methodValue}
          </MenuItem>
        ))}
      </Select>
    </GreenSelectForm>
  );
};
const CountryValueComponent = ({ valueStr, selectValueStr, disabled, error }) => {
  const getName = (code) => {
    let label;
    countries?.forEach((country) => {
      if (country?.value === code) label = country?.label;
    });
    return label;
  };
  return (
    <Autocomplete
      fullWidth
      options={countries}
      sx={{ border: `solid 1px ${error ? colors.red[500] : colors.neutral[200]}`, borderRadius: "8px" }}
      autoHighlight
      disabled={disabled}
      onChange={selectValueStr}
      value={getName(valueStr)}
      renderOption={(props, option) => (
        <Box
          component="option"
          value={option.value}
          {...props}
          sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}
        >
          {option.label} ({option.value})
        </Box>
      )}
      renderInput={(params) => (
        <SafeTextField
          {...params}
          placeholder="Select a country"
          sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}
        />
      )}
    />
  );
};
const ValueComponent = ({ keyStr, valueStr, selectValueStr, onBlur, disabled, padx, pady, error, helperText }) => {
  let isNumber = false;
  switch (keyStr) {
    case ExpressionKeyField.AS_NUMBER:
    case ExpressionKeyField.BOT_SCORE:
      isNumber = true;
      break;
    default:
      isNumber = false;
      break;
  }
  const type = isNumber ? "number" : "text";
  return (
    <SafeTextField
      label="Value"
      fullWidth
      type={type}
      value={valueStr}
      disabled={disabled}
      onChange={selectValueStr}
      onBlur={onBlur}
      key={`_value_${padx}_${pady}`}
      error={Boolean(error)}
      helperText={error || helperText || ""}
    />
  );
};

const CharacteristicsKeyComponent = ({ keyStr, selectKeyStr, padx, pady, advanced, error }) => {
  return (
    <GreenSelectForm error={error}>
      <InputLabel id="query-selector">Query</InputLabel>
      <Select labelId="query-selector" IconComponent={ArrowdownIcon} value={keyStr} onChange={selectKeyStr} fullWidth>
        {keyStr === "none" && (
          <MenuItem key={"_characteristics0_" + padx + "_" + pady} value="none" disabled sx={{ ...menuItemSx, display: "none!important" }}>
            Select Field ...
          </MenuItem>
        )}
        <MenuItem key={"_characteristics1_" + padx + "_" + pady} value={RateLimitCharacteristicKey.IP} sx={menuItemSx}>
          IP
        </MenuItem>
        {true === advanced && (
          <MenuItem key={"_characteristics2_" + padx + "_" + pady} value={RateLimitCharacteristicKey.IP_WITH_NAT} sx={menuItemSx}>
            IP with NAT support
          </MenuItem>
        )}
        {true === advanced && (
          <MenuItem key={"_characteristics3_" + padx + "_" + pady} value={RateLimitCharacteristicKey.QUERY} sx={menuItemSx}>
            Query
          </MenuItem>
        )}
        {true === advanced && (
          <MenuItem key={"_characteristics4_" + padx + "_" + pady} value={RateLimitCharacteristicKey.HEADERS} sx={menuItemSx}>
            Headers
          </MenuItem>
        )}

        {true === advanced && (
          <MenuItem key={"_characteristics5_" + padx + "_" + pady} value={RateLimitCharacteristicKey.COOKIE} sx={menuItemSx}>
            Cookie
          </MenuItem>
        )}

        {true === advanced && (
          <MenuItem key={"_characteristics6_" + padx + "_" + pady} value={RateLimitCharacteristicKey.ASN} sx={menuItemSx}>
            ASN
          </MenuItem>
        )}

        {true === advanced && (
          <MenuItem key={"_characteristics7_" + padx + "_" + pady} value={RateLimitCharacteristicKey.COUNTRY} sx={menuItemSx}>
            Country
          </MenuItem>
        )}

        {true === advanced && (
          <MenuItem key={"_characteristics8_" + padx + "_" + pady} value={RateLimitCharacteristicKey.JA3_FINGERPRINT} sx={menuItemSx}>
            JA3 Fingerprint*
          </MenuItem>
        )}
        {/* RL-V2 picker patch (2026-06-07) — closes the engine-vs-UI
            vocabulary gap. All 8 keys below were already supported by
            the data plane (src/rate_limit/matcher.rs resolve()) but
            unreachable through the panel. cred_stuffing in particular
            gates the multi-pod-consistent counter shipped in RL-V2.2. */}
        {true === advanced && (
          <MenuItem key={"_characteristics9_" + padx + "_" + pady} value={RateLimitCharacteristicKey.JA4_FINGERPRINT} sx={menuItemSx}>
            JA4 Fingerprint*
          </MenuItem>
        )}
        {true === advanced && (
          <MenuItem key={"_characteristics10_" + padx + "_" + pady} value={RateLimitCharacteristicKey.HOST} sx={menuItemSx}>
            Host
          </MenuItem>
        )}
        {true === advanced && (
          <MenuItem key={"_characteristics11_" + padx + "_" + pady} value={RateLimitCharacteristicKey.URI_PATH} sx={menuItemSx}>
            URI path
          </MenuItem>
        )}
        {true === advanced && (
          <MenuItem key={"_characteristics12_" + padx + "_" + pady} value={RateLimitCharacteristicKey.UID} sx={menuItemSx}>
            Auth UID
          </MenuItem>
        )}
        {true === advanced && (
          <MenuItem key={"_characteristics13_" + padx + "_" + pady} value={RateLimitCharacteristicKey.FINGERPRINT} sx={menuItemSx}>
            Composite fingerprint
          </MenuItem>
        )}
        {true === advanced && (
          <MenuItem key={"_characteristics14_" + padx + "_" + pady} value={RateLimitCharacteristicKey.CITY} sx={menuItemSx}>
            City
          </MenuItem>
        )}
        {true === advanced && (
          <MenuItem key={"_characteristics15_" + padx + "_" + pady} value={RateLimitCharacteristicKey.CRED_STUFFING} sx={menuItemSx}>
            Cred-stuffing (≥5 failures)
          </MenuItem>
        )}
        {true === advanced && (
          <MenuItem key={"_characteristics16_" + padx + "_" + pady} value={RateLimitCharacteristicKey.API_ANOMALY} sx={menuItemSx}>
            API anomaly (z≥3.0)
          </MenuItem>
        )}
      </Select>
    </GreenSelectForm>
  );
};

const CharacteristicsValueComponent = ({ valueStr, selectValueStr, onBlur, disabled, pady, error, helperText }) => {
  return (
    <SafeTextField
      label="Request"
      fullWidth
      value={valueStr}
      disabled={disabled}
      onChange={selectValueStr}
      onBlur={onBlur}
      key={"_characteristics_value_" + pady}
      error={Boolean(error)}
      helperText={error || helperText || ""}
    />
  );
};

export const RateLimitingRuleModal = ({ siteUid, open, handleClose, rateLimitRuleToEdit }) => {
  const { isAuthenticated, isFeatureEnabled, userType } = useAuth();
  const { updateRateLimit, addNewRateLimit, getCurrentRateLimit, setErr, errMsg, getRateLimits } = useRateLimit();
  const [ratelimitName, setRateLimitName] = useState("");
  const [conditions, setConditions] = useState([[{ key: "none", condition: "none", value: "" }]]);
  const [characteristics, setCharacteristics] = useState([{ key: "none", value: "" }]);

  const [action, setAction] = useState(FirewallAction.BLOCK);
  const [mitigationTimeout, setMitigationTimeout] = useState(RateLimitMitigationTimeout.THIRTY_SECONDS);
  const [requestsPerPeriod, setRequestsPerPeriod] = useState(1);
  const [period, setPeriod] = useState(RateLimitPeriod.TEN_SECONDS);

  // Phase-1 data-plane fields.
  const [mode, setMode] = useState(RateLimitMode.SHADOW);
  const [challengeKind, setChallengeKind] = useState(ChallengeKind.MANAGED);
  const [expiresAt, setExpiresAt] = useState("");
  const [expiresAtError, setExpiresAtError] = useState("");
  const [mitigationRecoveryPct, setMitigationRecoveryPct] = useState(25);
  const [difficulty, setDifficulty] = useState(1);
  const [burst, setBurst] = useState(0);
  const [blockMessage, setBlockMessage] = useState("");

  // Cred-stuffing carve-out (auth_failure) — closed taxonomy from feedback_ratelimit_locked.md.
  const [authFailureEnabled, setAuthFailureEnabled] = useState(false);
  const [authFailureKinds, setAuthFailureKinds] = useState([]);
  const [authFailureIgnoreClockSuspect, setAuthFailureIgnoreClockSuspect] = useState(true);
  // Whether API Security is enabled at the site level. Drives visibility of
  // the cred-stuffing carve-out below; the carve-out reads ctx.auth.error_kind,
  // which is only ever set when the API Security auth module applies a policy
  // to the path. With API Security off site-wide, the carve-out is guaranteed
  // to be inert, so hide it from operators entirely. null = loading.
  const [apiSecurityEnabled, setApiSecurityEnabled] = useState(null);

  const [nameError, setNameError] = useState(false);
  const [conditionErrors, setConditionErrors] = useState([[{ key: false, condition: false, value: false }]]);
  const [characteristicErrors, setCharacteristicErrors] = useState([{ key: false, value: false }]);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [cellTouched, setCellTouched] = useState({});
  const [baselineKey, setBaselineKey] = useState(0);

  const touchCell = (key) => {
    setCellTouched((prev) => ({ ...prev, [key]: true }));
  };

  const isCellTouched = (key) => Boolean(cellTouched[key]);

  const showCondCellError = (i, j, field, errorMsg, andCond) =>
    resolveVisibleCellError(errorMsg, {
      touched: isCellTouched(`${i}.${j}.${field}`),
      submitAttempted,
      rowStarted: isExpressionRowStarted(andCond),
      valueEntered: field === "value" && String(andCond?.value ?? "").trim() !== "",
    });

  const isCharacteristicRowStarted = (characteristic) =>
    Boolean(characteristic?.key && characteristic.key !== "none");

  const showCharCellError = (j, field, errorMsg, characteristic) =>
    resolveVisibleCellError(errorMsg, {
      touched: isCellTouched(`c.${j}.${field}`),
      submitAttempted,
      rowStarted: isCharacteristicRowStarted(characteristic),
      valueEntered: field === "value" && String(characteristic?.value ?? "").trim() !== "",
    });

  const formValidation = useMemo(
    () => validateRateLimitRuleForm({ name: ratelimitName, conditions, characteristics, blockMessage }),
    [ratelimitName, conditions, characteristics, blockMessage],
  );

  const showNameErr = submitAttempted && formValidation.formErrors.name;
  const showConditionsErr = submitAttempted && formValidation.formErrors.conditions;
  const showCharacteristicsErr = submitAttempted && formValidation.formErrors.characteristics;

  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const toIsoOrNull = (local) => {
    if (!local) return null;
    const d = new Date(local);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  };
  const toggleAuthFailureKind = (kind) => {
    setAuthFailureKinds((prev) => (prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind]));
  };

  // Fetch site-level API Security master toggle when the modal opens.
  // Drives visibility of the cred-stuffing carve-out. On error/404 we treat
  // it as disabled; the carve-out depends on src/auth/ output, so hiding
  // it when uncertain is the safe default.
  useEffect(() => {
    if (!open || !siteUid) return undefined;
    let cancelled = false;
    userAxios.get(`/asec/${siteUid}/master`)
      .then((r) => { if (!cancelled) setApiSecurityEnabled(!!r.data?.enabled); })
      .catch(() => { if (!cancelled) setApiSecurityEnabled(false); });
    return () => { cancelled = true; };
  }, [open, siteUid]);

  useEffect(() => {
    if (!open) return undefined;
    window.scrollTo(0, 0);
    const init = async () => {
      if (rateLimitRuleToEdit) {
        const detailedRule = await getCurrentRateLimit(siteUid, rateLimitRuleToEdit?.id);
        setRateLimitName(detailedRule?.name ? detailedRule?.name : undefined);
        const conds = detailedRule?.conditions && 0 < detailedRule.conditions.length && 0 < detailedRule.conditions[0].length
            ? detailedRule.conditions
            : [[{ key: "none", condition: "none", value: "" }]];
        setConditions(conds);
        setConditionErrors(conds.map((orCond) => orCond.map(() => ({ key: false, condition: false, value: false }))));
        const chars = detailedRule?.characteristics || [{ key: "none", value: "" }];
        setCharacteristics(chars);
        setCharacteristicErrors(chars.map(() => ({ key: false, value: false })));
        setAction(detailedRule?.action ? detailedRule?.action : FirewallAction.BLOCK);
        setRequestsPerPeriod(detailedRule?.requests_per_period ? detailedRule.requests_per_period : 1);
        setPeriod(detailedRule?.period ? detailedRule.period : RateLimitPeriod.TEN_SECONDS);
        setMitigationTimeout(
          detailedRule?.mitigation_timeout ? detailedRule.mitigation_timeout : RateLimitMitigationTimeout.THIRTY_SECONDS
        );
        setMode(detailedRule?.mode ?? RateLimitMode.SHADOW);
        setChallengeKind(detailedRule?.challenge_kind ?? ChallengeKind.MANAGED);
        setExpiresAt(toLocalInput(detailedRule?.expires_at));
        setMitigationRecoveryPct(detailedRule?.mitigation_recovery_pct ?? 25);
        setDifficulty(detailedRule?.difficulty ?? 1);
        setBurst(detailedRule?.burst ?? 0);
        setBlockMessage(detailedRule?.block_message ?? "");
        const af = detailedRule?.auth_failure;
        if (af && typeof af === "object") {
          setAuthFailureEnabled(true);
          setAuthFailureKinds(Array.isArray(af.kinds) ? af.kinds : []);
          setAuthFailureIgnoreClockSuspect(af.ignore_clock_suspect ?? true);
        } else {
          setAuthFailureEnabled(false);
          setAuthFailureKinds([]);
          setAuthFailureIgnoreClockSuspect(true);
        }
      } else {
        setRateLimitName("");
        setConditions([[{ key: "none", condition: "none", value: "" }]]);
        setConditionErrors([[{ key: false, condition: false, value: false }]]);
        setCharacteristics([{ key: "none", value: "" }]);
        setCharacteristicErrors([{ key: false, value: false }]);
        setAction(FirewallAction.BLOCK);
        setRequestsPerPeriod(1);
        setPeriod(RateLimitPeriod.TEN_SECONDS);
        setMitigationTimeout(RateLimitMitigationTimeout.THIRTY_SECONDS);
        setMode(RateLimitMode.SHADOW);
        setChallengeKind(ChallengeKind.MANAGED);
        setExpiresAt("");
        setMitigationRecoveryPct(25);
        setDifficulty(1);
        setBurst(0);
        setBlockMessage("");
        setAuthFailureEnabled(false);
        setAuthFailureKinds([]);
        setAuthFailureIgnoreClockSuspect(true);
      }
      setNameError(false);
      setSubmitAttempted(false);
      setCellTouched({});
      setBaselineKey((k) => k + 1);
    };
    if (isAuthenticated) init();
    return () => setErr(null);
  }, [open, rateLimitRuleToEdit, getCurrentRateLimit, isAuthenticated, siteUid, setErr]);

  const ratelimitNmaeChange = (event) => {
    setRateLimitName(event.target.value);
    if (event.target.value.trim() !== "") setNameError(false);
  };

  const selectKeyStr = (event, i, j) => {
    let list = [...conditions];
    list[i][j]["key"] = event.target.value;
    setConditions(list);
    let errList = [...conditionErrors];
    if (errList[i] && errList[i][j]) {
      errList[i][j].key = false;
      setConditionErrors(errList);
    }
  };
  const selectCondStr = (event, i, j) => {
    let list = [...conditions];
    list[i][j]["condition"] = event.target.value;
    setConditions(list);
    let errList = [...conditionErrors];
    if (errList[i] && errList[i][j]) {
      errList[i][j].condition = false;
      setConditionErrors(errList);
    }
  };
  const selectValueStr = (event, i, j) => {
    let list = [...conditions];
    list[i][j]["value"] = event.target.value;
    setConditions(list);
    let errList = [...conditionErrors];
    if (errList[i] && errList[i][j]) {
      errList[i][j].value = false;
      setConditionErrors(errList);
    }
  };
  const selectCountryStr = (event, newValue, i, j) => {
    let list = [...conditions];
    list[i][j]["value"] = newValue?.value;
    setConditions(list);
    let errList = [...conditionErrors];
    if (errList[i] && errList[i][j]) {
      errList[i][j].value = false;
      setConditionErrors(errList);
    }
  };

  const andClick = (i, j) => {
    let list = [...conditions];
    list[i].splice(j + 1, 0, { key: "none", condition: "none", value: "" });
    setConditions(list);
    let errList = [...conditionErrors];
    if (errList[i]) {
      errList[i].splice(j + 1, 0, { key: false, condition: false, value: false });
      setConditionErrors(errList);
    }
  };

  const orClick = () => {
    setConditions([...conditions, [{ key: "none", condition: "none", value: "" }]]);
    setConditionErrors([...conditionErrors, [{ key: false, condition: false, value: false }]]);
  };

  const removeClick = (i, j) => {
    let list = [...conditions];
    list[i].splice(j, 1);
    if (list[i].length === 0) list.splice(i, 1);
    if (0 === list.length) {
      list.push([{ key: "none", condition: "none", value: "" }]);
    }
    setConditions(list);
    let errList = [...conditionErrors];
    if (errList[i]) {
      errList[i].splice(j, 1);
      if (errList[i].length === 0) errList.splice(i, 1);
      if (0 === errList.length) {
        errList.push([{ key: false, condition: false, value: false }]);
      }
      setConditionErrors(errList);
    }
  };

  const selectCharacteristicKeyStr = (event, j) => {
    let list = [...characteristics];
    list[j]["key"] = event.target.value;
    setCharacteristics(list);
    let errList = [...characteristicErrors];
    if (errList[j]) {
      errList[j].key = false;
      setCharacteristicErrors(errList);
    }
  };
  const selectCharacteristicValueStr = (event, j) => {
    let list = [...characteristics];
    list[j]["value"] = event.target.value;
    setCharacteristics(list);
    let errList = [...characteristicErrors];
    if (errList[j]) {
      errList[j].value = false;
      setCharacteristicErrors(errList);
    }
  };

  const andCharacteristicClick = () => {
    setCharacteristics([...characteristics, { key: "none", value: "" }]);
    setCharacteristicErrors([...characteristicErrors, { key: false, value: false }]);
  };
  const removeCharacteristicClick = (j) => {
    let list = [...characteristics];
    list.splice(j, 1);
    if (0 === list.length) {
      list.push({ key: "none", value: "" });
    }
    setCharacteristics(list);
    let errList = [...characteristicErrors];
    errList.splice(j, 1);
    if (0 === errList.length) {
      errList.push({ key: false, value: false });
    }
    setCharacteristicErrors(errList);
  };

  const selectRequstsPerPeriod = (event) => {
    setRequestsPerPeriod(event.target.value);
  };

  const selectPeriod = (event) => {
    setPeriod(event.target.value);
  };

  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = React.useState(false);

  const formSnapshot = useMemo(
    () => ({
      ratelimitName,
      conditions,
      characteristics,
      action,
      mitigationTimeout,
      requestsPerPeriod,
      period,
      mode,
      challengeKind,
      expiresAt,
      mitigationRecoveryPct,
      difficulty,
      burst,
      blockMessage,
      authFailureEnabled,
      authFailureKinds,
      authFailureIgnoreClockSuspect,
    }),
    [
      ratelimitName,
      conditions,
      characteristics,
      action,
      mitigationTimeout,
      requestsPerPeriod,
      period,
      mode,
      challengeKind,
      expiresAt,
      mitigationRecoveryPct,
      difficulty,
      burst,
      blockMessage,
      authFailureEnabled,
      authFailureKinds,
      authFailureIgnoreClockSuspect,
    ],
  );
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);
  const { requestClose, confirmDialog } = useGuardedDrawerClose(handleClose, isDirty, { disabled: loading });

  const handleSave = async () => {
    if (userType === UserType.ORG_USER) return;
    setSubmitAttempted(true);
    const validation = validateRateLimitRuleForm({
      name: ratelimitName,
      conditions,
      characteristics,
      blockMessage,
    });
    const expiresValidation = validateExpiresAt(expiresAt);
    setExpiresAtError(expiresValidation.valid ? "" : expiresValidation.message);
    if (!validation.valid || !expiresValidation.valid) {
      setConditionErrors(validation.conditionErrors);
      setCharacteristicErrors(validation.characteristicErrors);
      return;
    }
    const conditionEmpty = validation.conditionEmpty;
    setLoading(true);
    try {
      const resolvedAction = Number(action) || FirewallAction.BLOCK;
      const resolvedBurst = Number(burst) || 0;
      const body = {
        name: ratelimitName,
        conditions: conditionEmpty ? [[]] : conditions,
        action: resolvedAction,
        mitigation_timeout: Number(mitigationTimeout),
        requests_per_period: Number(requestsPerPeriod),
        period: Number(period),
        characteristics: characteristics,
        mode: Number(mode),
        expires_at: toIsoOrNull(expiresAt),
        mitigation_recovery_pct: Number(mitigationRecoveryPct),
        difficulty: Number(difficulty),
        block_message: blockMessage || null,
        auth_failure: authFailureEnabled
          ? { kinds: authFailureKinds, ignore_clock_suspect: authFailureIgnoreClockSuspect }
          : null,
      };
      if (rateLimitRuleToEdit) {
        body.burst = resolvedBurst;
      } else if (resolvedBurst > 0) {
        body.burst = resolvedBurst;
      }
      if (resolvedAction === FirewallAction.CHALLENGE) {
        body.challenge_kind = challengeKind;
      }
      let result = {};
      if (rateLimitRuleToEdit) {
        result = await updateRateLimit(siteUid, rateLimitRuleToEdit?.id, body);
      } else {
        result = await addNewRateLimit(siteUid, body);
      }
      if (result?.status === "success") {
        showSuccess(result?.msg || "Rate limit rule saved successfully");
        getRateLimits(siteUid, false);
        handleClose();
      } else {
        showError(result?.msg || "Failed to save rate limit rule");
      }
    } catch (err) {
      showError(err?.message || "Failed to save rate limit rule");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (errMsg) {
      showError(errMsg);
    }
  }, [errMsg, showError]);

  return (
    <>
      <Modal 
        open={open} 
        onClose={requestClose}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
          }
        }}
      >
        <Slide direction="left" in={open} mountOnEnter unmountOnExit>
          <Stack
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "33%",
              minWidth: 480,
              height: "100vh",
              backgroundColor: "#fff",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
              zIndex: 1300,
              borderTopLeftRadius: "8px",
            }}
          >
            <Stack direction={"row"} justifyContent={"space-between"} spacing={6} sx={{ px: 4, pt: 4, pb: 3 }}>
              <Stack direction={"row"} alignItems={"center"} spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "8px",
                    backgroundColor: "#EEF0FE",
                  }}
                >
                  <ExceptIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                </Box>
                <Stack>
                  <Stack direction="row" alignItems="center" sx={{ flexWrap: "nowrap" }}>
                    <Typography
                      sx={ { fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}
                    >
                      {rateLimitRuleToEdit ? "Edit rate limiting rule" : "Add rate limiting rule"}
                    </Typography>
                    <FormHelpTooltip tooltip="Define when to count requests (URL, method, headers) and what happens when the limit is exceeded — block, challenge, or log. Protects origin from burst traffic." />
                  </Stack>
                  <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                    Set conditions to throttle or block excessive requests.
                  </Typography>
                </Stack>
              </Stack>
              <IconButton onClick={requestClose} aria-label="Close" sx={{ p: 0 }}>
                <CloseIcon style={{ color: colors.neutral[600] }} />
              </IconButton>
            </Stack>
            <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 130px)` }}>
              <ReactPerfectScrollbar>
                <Stack justifyContent={"space-between"} spacing={4} sx={{ pr: 4, height: "100%" }}>
                  <Stack spacing={4}>
                    <SafeTextField fullWidth label="Rate limiting rule name" value={ratelimitName} onChange={ratelimitNmaeChange} error={Boolean(showNameErr)} helperText={showNameErr || ""} />
                    {conditions.map((orCond, i) => {
                      return (
                        <Box
                          key={`cond_${i}`}
                          sx={{ width: "100%", p: 4, borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}` }}
                        >
                          <Grid container spacing={3}>
                            {orCond.map((andCond, j) => {
                              const cellErr = formValidation.conditionErrors[i]?.[j] || {};
                              const keyErr = showCondCellError(i, j, "key", cellErr.key, andCond);
                              const condErr = showCondCellError(i, j, "condition", cellErr.condition, andCond);
                              const valueErr = showCondCellError(i, j, "value", cellErr.value, andCond);
                              const valueHint = getExpressionValueHint(andCond.key);
                              return (
                                <React.Fragment key={`andCond_${i}_${j}`}>
                                  <Grid item xs={12}>
                                    <Typography sx={ { fontSize: 16, lineHeight: "24px", color: colors.neutral[1000] }}>
                                      {j === 0 ? (i === 0 ? "When incoming requests match..." : "Or") : "And"}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Grid container spacing={3}>
                                      <Grid item xs={12} sm={6}>
                                        <Box onBlur={() => touchCell(`${i}.${j}.key`)}>
                                          <KeyComponent
                                            keyStr={andCond.key}
                                            selectKeyStr={(event) => {
                                              touchCell(`${i}.${j}.key`);
                                              selectKeyStr(event, i, j);
                                            }}
                                            padx={i}
                                            pady={j}
                                            error={keyErr}
                                          />
                                          <ExpressionFieldMessage error={keyErr} />
                                        </Box>
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <Box onBlur={() => touchCell(`${i}.${j}.condition`)}>
                                          <CondComponent
                                            condStr={andCond.condition}
                                            selectCondStr={(event) => {
                                              touchCell(`${i}.${j}.condition`);
                                              selectCondStr(event, i, j);
                                            }}
                                            disableMenu={getValidCondFlag(andCond.key)}
                                            disabled={andCond.key === "none"}
                                            padx={i}
                                            pady={j}
                                            error={condErr}
                                          />
                                          <ExpressionFieldMessage error={condErr} />
                                        </Box>
                                      </Grid>
                                      <Grid item xs={12}>
                                        {andCond.key === ExpressionKeyField.METHOD ? (
                                          <Box onBlur={() => touchCell(`${i}.${j}.value`)}>
                                            <MethodValueComponent
                                              valueStr={andCond.value}
                                              selectValueStr={(event) => {
                                                touchCell(`${i}.${j}.value`);
                                                selectValueStr(event, i, j);
                                              }}
                                              disabled={andCond.key === ExpressionKeyField.NONE || andCond.condition === "none"}
                                              padx={i}
                                              pady={j}
                                              error={valueErr}
                                            />
                                            <ExpressionFieldMessage error={valueErr} hint={valueHint} />
                                          </Box>
                                        ) : andCond.key === ExpressionKeyField.COUNTRY ? (
                                          <Box onBlur={() => touchCell(`${i}.${j}.value`)}>
                                            <CountryValueComponent
                                              valueStr={andCond.value}
                                              selectValueStr={(event, newValue) => {
                                                touchCell(`${i}.${j}.value`);
                                                selectCountryStr(event, newValue, i, j);
                                              }}
                                              disabled={andCond.key === "none" || andCond.condition === "none"}
                                              error={valueErr}
                                            />
                                            <ExpressionFieldMessage error={valueErr} hint={valueHint} />
                                          </Box>
                                        ) : (
                                          <ValueComponent
                                            keyStr={andCond.key}
                                            valueStr={andCond.value}
                                            selectValueStr={(event) => {
                                              touchCell(`${i}.${j}.value`);
                                              selectValueStr(event, i, j);
                                            }}
                                            onBlur={() => touchCell(`${i}.${j}.value`)}
                                            disabled={andCond.key === ExpressionKeyField.NONE || andCond.condition === "none"}
                                            padx={i}
                                            pady={j}
                                            error={valueErr}
                                            helperText={valueHint}
                                          />
                                        )}
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Stack width={"100%"} alignItems={"center"} direction="row" justifyContent="end" spacing={4}>
                                          <Stack
                                            direction={"row"}
                                            alignItems={"center"}
                                            spacing={1}
                                            onClick={() => andClick(i, j)}
                                            sx={{
                                              cursor: "pointer",
                                              borderRadius: "8px",
                                              border: `solid 1px ${colors.secondary[300]}`,
                                              px: 2,
                                              py: 1,
                                            }}
                                          >
                                            <DuplicateIcon style={{ color: colors.neutral[800] }} />
                                            <Typography
                                              sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}
                                            >
                                              And
                                            </Typography>
                                          </Stack>
                                          <Box onClick={() => removeClick(i, j)} sx={{ cursor: "pointer" }}>
                                            <DeleteIcon style={{ color: colors.neutral[600] }} />
                                          </Box>
                                        </Stack>
                                      </Grid>
                                      {conditions.length - 1 === i && orCond.length - 1 === j && (
                                        <Grid item xs={12}>
                                          <Button
                                            startIcon={<PlusIcon style={{ color: "#fff" }} />}
                                            onClick={orClick}
                                            sx={{
                                              height: 32,
                                              width: 105,
                                              ...containedButtonSx,
                                            }}
                                          >
                                            Add new
                                          </Button>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Grid>
                                </React.Fragment>
                              );
                            })}
                          </Grid>
                        </Box>
                      );
                    })}
                    {showConditionsErr && (
                      <Typography sx={{ fontSize: 12, color: "#d32f2f", mt: -2 }}>
                        {formValidation.formErrors.conditions}
                      </Typography>
                    )}
                    <Box sx={{ width: "100%", p: 4, borderRadius: "8px", backgroundColor: colors.primary["alpha"] }}>
                      <Grid container spacing={3}>
                        <Grid item xs={6}>
                          <Stack spacing={1}>
                            <Typography
                              sx={{
                                fontWeight: "medium",
                                fontSize: 16,
                                lineHeight: "24px",
                                color: colors.neutral[1000],
                              }}
                            >
                              Then
                            </Typography>
                            <ActionComponent action={action} setAction={setAction} />
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack spacing={1}>
                            <Typography
                              sx={{
                                fontWeight: "medium",
                                fontSize: 16,
                                lineHeight: "24px",
                                color: colors.neutral[1000],
                              }}
                            >
                              For
                            </Typography>
                            <GreenSelectForm>
                              <InputLabel id="duration-selector">Duration</InputLabel>
                              <Select
                                labelId="duration-selector"
                                IconComponent={ArrowdownIcon}
                                value={mitigationTimeout}
                                onChange={(event) => setMitigationTimeout(event.target.value)}
                                fullWidth
                              >
                                <MenuItem key={"mitigation_timeout_30"} value={RateLimitMitigationTimeout.THIRTY_SECONDS} sx={menuItemSx}>
                                  30 Seconds
                                </MenuItem>
                                <MenuItem key={"mitigation_timeout_60"} value={RateLimitMitigationTimeout.ONE_MINUTE} sx={menuItemSx}>
                                  1 Minute
                                </MenuItem>
                                <MenuItem key={"mitigation_timeout_600"} value={RateLimitMitigationTimeout.TEN_MINUTES} sx={menuItemSx}>
                                  10 Minutes
                                </MenuItem>
                                <MenuItem key={"mitigation_timeout_3600"} value={RateLimitMitigationTimeout.ONE_HOUR} sx={menuItemSx}>
                                  1 Hour
                                </MenuItem>
                                <MenuItem key={"mitigation_timeout_86400"} value={RateLimitMitigationTimeout.ONE_DAY} sx={menuItemSx}>
                                  1 Day
                                </MenuItem>
                              </Select>
                            </GreenSelectForm>
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack spacing={1}>
                            <Typography
                              sx={{
                                fontWeight: "medium",
                                fontSize: 16,
                                lineHeight: "24px",
                                color: colors.neutral[1000],
                              }}
                            >
                              When rate exceeds
                            </Typography>
                            <SafeTextField sanitize={false} label={"Request"} value={requestsPerPeriod} onChange={selectRequstsPerPeriod} fullWidth />
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack justifyContent={"flex-end"} sx={{ height: "100%" }}>
                            <GreenSelectForm>
                              <InputLabel id="period-selector">Period</InputLabel>
                              <Select
                                labelId="period-selector"
                                IconComponent={ArrowdownIcon}
                                value={period}
                                onChange={selectPeriod}
                                fullWidth
                              >
                                <MenuItem key={"period_10"} value={RateLimitPeriod.TEN_SECONDS} sx={menuItemSx}>
                                  10 Seconds
                                </MenuItem>
                                <MenuItem key={"period_60"} value={RateLimitPeriod.ONE_MINUTE} sx={menuItemSx}>
                                  1 Minute
                                </MenuItem>
                                <MenuItem key={"period_120"} value={RateLimitPeriod.TWO_MINUTES} sx={menuItemSx}>
                                  2 Minutes
                                </MenuItem>
                                <MenuItem key={"period_300"} value={RateLimitPeriod.FIVE_MINUTES} sx={menuItemSx}>
                                  5 Minutes
                                </MenuItem>
                                <MenuItem key={"period_600"} value={RateLimitPeriod.TEN_MINUTES} sx={menuItemSx}>
                                  10 Minutes
                                </MenuItem>
                                {isFeatureEnabled(FeatureId.ADVANCED_FEATURES_IN_RATE_LIMIT_RULE) && (
                                  <MenuItem key={"period_3600"} value={RateLimitPeriod.ONE_HOUR} sx={menuItemSx}>
                                    1 Hour
                                  </MenuItem>
                                )}
                              </Select>
                            </GreenSelectForm>
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography
                            sx={{
                              fontWeight: "medium",
                              fontSize: 16,
                              lineHeight: "24px",
                              color: colors.neutral[1000],
                            }}
                          >
                            With the same
                          </Typography>
                        </Grid>
                        {characteristics.map((characteristic, j) => {
                          const charErr = formValidation.characteristicErrors[j] || {};
                          const charKeyErr = showCharCellError(j, "key", charErr.key, characteristic);
                          const charValueErr = showCharCellError(j, "value", charErr.value, characteristic);
                          const charValueHint = RATE_LIMIT_CHAR_HINTS[characteristic.key] || "";
                          return (
                            <React.Fragment key={`characteristic_${j}`}>
                              {j > 0 && (
                                <Grid item xs={12}>
                                  <Typography
                                    sx={{
                                      fontWeight: "medium",
                                      fontSize: 16,
                                      lineHeight: "24px",
                                      color: colors.neutral[1000],
                                    }}
                                  >
                                    And
                                  </Typography>
                                </Grid>
                              )}
                              <Grid item xs={6}>
                                <Box onBlur={() => touchCell(`c.${j}.key`)}>
                                  <CharacteristicsKeyComponent
                                    keyStr={characteristic.key}
                                    selectKeyStr={(event) => {
                                      touchCell(`c.${j}.key`);
                                      selectCharacteristicKeyStr(event, j);
                                    }}
                                    pady={j}
                                    advanced={isFeatureEnabled(FeatureId.ADVANCED_FEATURES_IN_RATE_LIMIT_RULE)}
                                    error={charKeyErr}
                                  />
                                  <ExpressionFieldMessage error={charKeyErr} />
                                </Box>
                              </Grid>
                              {(characteristic.key === RateLimitCharacteristicKey.QUERY ||
                                characteristic.key === RateLimitCharacteristicKey.HEADERS ||
                                characteristic.key === RateLimitCharacteristicKey.COOKIE) && (
                                <Grid item xs={6}>
                                  <CharacteristicsValueComponent
                                    valueStr={characteristic.value}
                                    selectValueStr={(event) => {
                                      touchCell(`c.${j}.value`);
                                      selectCharacteristicValueStr(event, j);
                                    }}
                                    onBlur={() => touchCell(`c.${j}.value`)}
                                    disabled={characteristic.key === "none"}
                                    pady={j}
                                    error={charValueErr}
                                    helperText={charValueHint}
                                  />
                                </Grid>
                              )}
                              <Grid item xs={12}>
                                <Stack direction={"row"} alignItems={"center"} justifyContent={"flex-end"} spacing={2}>
                                  {characteristics.length - 1 === j && (
                                    <Stack
                                      direction={"row"}
                                      alignItems={"center"}
                                      spacing={1}
                                      onClick={andCharacteristicClick}
                                      sx={{
                                        cursor: "pointer",
                                        borderRadius: "8px",
                                        border: `solid 1px ${colors.secondary[300]}`,
                                        px: 2,
                                        py: 1,
                                        width: "fit-content",
                                      }}
                                    >
                                      <DuplicateIcon style={{ color: colors.neutral[800] }} />
                                      <Typography
                                        sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}
                                      >
                                        And
                                      </Typography>
                                    </Stack>
                                  )}
                                  <Box onClick={() => removeCharacteristicClick(j)} sx={{ cursor: "pointer" }}>
                                    <DeleteIcon style={{ color: colors.neutral[600] }} />
                                  </Box>
                                </Stack>
                              </Grid>
                            </React.Fragment>
                          );
                        })}
                        {showCharacteristicsErr && (
                          <Grid item xs={12}>
                            <Typography sx={{ fontSize: 12, color: "#d32f2f" }}>
                              {formValidation.formErrors.characteristics}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>

                    {/* Phase-1 data-plane fields */}
                    <Box sx={{ width: "100%", p: 4, borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}` }}>
                      <Grid container spacing={3}>
                        <Grid item xs={6}>
                          <GreenSelectForm>
                            <InputLabel id="rl-mode-label">Mode</InputLabel>
                            <Select
                              labelId="rl-mode-label"
                              IconComponent={ArrowdownIcon}
                              fullWidth
                              value={mode}
                              onChange={(e) => setMode(e.target.value)}
                            >
                              <MenuItem value={RateLimitMode.OFF} sx={menuItemSx}>Off</MenuItem>
                              <MenuItem value={RateLimitMode.ENFORCE} sx={menuItemSx}>Enforce</MenuItem>
                              <MenuItem value={RateLimitMode.SHADOW} sx={menuItemSx}>Shadow</MenuItem>
                            </Select>
                          </GreenSelectForm>
                        </Grid>
                        {action === FirewallAction.CHALLENGE && (
                          <Grid item xs={6}>
                            <GreenSelectForm>
                              <InputLabel id="rl-challenge-kind-label">Challenge kind</InputLabel>
                              <Select
                                labelId="rl-challenge-kind-label"
                                IconComponent={ArrowdownIcon}
                                fullWidth
                                value={challengeKind}
                                onChange={(e) => setChallengeKind(e.target.value)}
                              >
                                <MenuItem value={ChallengeKind.INVISIBLE} sx={menuItemSx}>Invisible</MenuItem>
                                <MenuItem value={ChallengeKind.MANAGED} sx={menuItemSx}>Managed</MenuItem>
                                <MenuItem value={ChallengeKind.ADAPTIVE} sx={menuItemSx}>Adaptive</MenuItem>
                                <MenuItem value={ChallengeKind.INTERACTIVE} sx={menuItemSx}>Interactive</MenuItem>
                              </Select>
                            </GreenSelectForm>
                          </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                          <GreenDateTimeLocalField
                            label="Expires at"
                            fullWidth
                            disablePast
                            value={expiresAt}
                            error={Boolean(expiresAtError)}
                            helperText={expiresAtError || ""}
                            onChange={(e) => {
                              setExpiresAt(e.target.value);
                              setExpiresAtError("");
                            }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <SafeTextField
                            label="Mitigation recovery %"
                            type="number"
                            fullWidth
                            value={mitigationRecoveryPct}
                            inputProps={{ min: 0, max: 100 }}
                            onChange={(e) => setMitigationRecoveryPct(Number(e.target.value))}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <SafeTextField
                            label="Difficulty"
                            type="number"
                            fullWidth
                            value={difficulty}
                            inputProps={{ min: 1, max: 1000000 }}
                            onChange={(e) => setDifficulty(Number(e.target.value))}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <SafeTextField
                            label="Burst"
                            type="number"
                            fullWidth
                            value={burst}
                            inputProps={{ min: 0 }}
                            onChange={(e) => setBurst(Number(e.target.value))}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <SafeTextField
                            label="Block message (max 500 chars)"
                            fullWidth
                            value={blockMessage}
                            inputProps={{ maxLength: 500 }}
                            onChange={(e) => setBlockMessage(e.target.value)}
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Cred-stuffing carve-out (auth_failure). Hidden when site-level
                        API Security is off, because the carve-out depends on the
                        auth verdict (ctx.auth.error_kind) emitted by src/auth/.
                        That field is only ever set when an API Security auth policy
                        matches the request, so with API Security off the rule is
                        guaranteed to be inert and surfacing it only invites confusion. */}
                    {apiSecurityEnabled && (
                    <Box sx={{ width: "100%", p: 4, borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}` }}>
                      <Stack spacing={3}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography
                            sx={{
                              fontWeight: "medium",
                              fontSize: 16,
                              lineHeight: "24px",
                              color: colors.neutral[1000],
                            }}
                          >
                            Auth failure carve-out (cred-stuffing)
                          </Typography>
                          <GreenSwitch
                            checked={authFailureEnabled}
                            onChange={(e) => setAuthFailureEnabled(e.target.checked)}
                          />
                        </Stack>
                        {authFailureEnabled && (
                          <>
                            <Stack
                              direction="row"
                              alignItems="flex-start"
                              spacing={2}
                              sx={{
                                p: 2,
                                borderRadius: "8px",
                                backgroundColor: colors.primary["alpha"],
                                border: `solid 1px ${colors.primary[300]}`,
                              }}
                            >
                              <Box sx={{ width: 20, height: 20, mt: "2px" }}>
                                <InfoIcon style={{ color: colors.primary[300], width: 20, height: 20 }} />
                              </Box>
                              <Typography sx={ { fontSize: 13, lineHeight: "19px", color: colors.primary[300] }}>
                                Requires an API Security auth policy configured on the matching path.
                                Without one this rule will never fire, because the carve-out reads
                                the auth verdict emitted by the API Security auth module and that
                                verdict is only set when a policy applies to the request.
                              </Typography>
                            </Stack>
                            <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                              Select which auth-failure kinds count toward this rate limit.
                            </Typography>
                            <Grid container spacing={1}>
                              {AuthFailureKind.map((kind) => (
                                <Grid item xs={6} sm={4} key={`auth_kind_${kind}`}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={authFailureKinds.includes(kind)}
                                        onChange={() => toggleAuthFailureKind(kind)}
                                        sx={{
                                          color: colors.neutral[400],
                                          "&.Mui-checked": { color: colors.primary[300] },
                                        }}
                                      />
                                    }
                                    label={
                                      <Typography sx={ { fontSize: 13, color: colors.neutral[800] }}>
                                        {kind}
                                      </Typography>
                                    }
                                  />
                                </Grid>
                              ))}
                            </Grid>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                              <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}>
                                Ignore clock-suspect failures
                              </Typography>
                              <GreenSwitch
                                checked={authFailureIgnoreClockSuspect}
                                onChange={(e) => setAuthFailureIgnoreClockSuspect(e.target.checked)}
                              />
                            </Stack>
                          </>
                        )}
                      </Stack>
                    </Box>
                    )}

                    <Stack spacing={4}>
                      <Stack
                        direction={"row"}
                        spacing={2}
                        sx={{
                          minHeight: 48,
                          p: 2,
                          borderRadius: "8px",
                          backgroundColor: colors.primary["alpha"],
                          border: `solid 1px ${colors.primary[300]}`,
                        }}
                      >
                        <Box sx={{ width: 24, height: 24 }}>
                          <InfoIcon style={{ color: colors.primary[300], width: 24, height: 24 }} />
                        </Box>
                        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                          You can update, review, or manage settings on this screen. Changes will be applied instantly after save!
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                  <Stack direction={"row"} alignItems={"center"} spacing={3}>
                    <SaveButton
                      onClick={handleSave}
                      loading={loading}
                      label="Apply"
                      sx={formSaveButtonSx}
                    />
                    <Button
                      variant="outlined"
                      onClick={requestClose}
                      sx={{
                        height: 44,
                        width: "50%",
                        borderRadius: "8px",
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.primary[300],
                        fontWeight: "regular",
                        textTransform: "none",
                        border: `solid 1px ${colors.primary[300]}`,
                        "&:hover": {
                          color: colors.primary[400],
                          borderColor: colors.primary[400],
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              </ReactPerfectScrollbar>
            </Box>
          </Stack>
        </Slide>
      </Modal>
      {confirmDialog}
    </>
  );
};
export default RateLimitingRuleModal;
